import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Tabs, Tab, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaMapMarkerAlt, FaInfoCircle, FaTicketAlt, FaUsers, FaBuilding, FaSearch, FaImage } from 'react-icons/fa';
import supabase from '../../supabaseClient'; // Importando o supabase client

const EventsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [eventos, setEventos] = useState([]);
  const [eventosPassados, setEventosPassados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categorias, setCategorias] = useState([]);
  
  // Buscar eventos do banco de dados
  useEffect(() => {
    async function fetchEventos() {
      try {
        setLoading(true);
        const dataAtual = new Date().toISOString().split('T')[0]; // Formato YYYY-MM-DD
        
        // Buscar eventos futuros (data_fim >= hoje)
        const { data: eventosFuturos, error: errorFuturos } = await supabase
          .from('caex_eventos')
          .select('*')
          .gte('data_fim', dataAtual)
          .eq('ativo', true)
          .order('data_inicio', { ascending: true });
        
        if (errorFuturos) throw errorFuturos;
        
        // Buscar eventos passados (data_fim < hoje)
        const { data: eventosAnteriores, error: errorAnteriores } = await supabase
          .from('caex_eventos')
          .select('*')
          .lt('data_fim', dataAtual)
          .eq('ativo', true)
          .order('data_inicio', { ascending: false })
          .limit(6); // Limitando a 6 eventos passados
        
        if (errorAnteriores) throw errorAnteriores;
        
        // Adicionar categorias aleatórias para cada evento (simulação)
        const categoriasDisponiveis = [
          'Construção', 'Tecnologia', 'Agronegócio', 
          'Alimentação', 'Energia', 'Design', 'Saúde', 
          'Educação', 'Automobilístico'
        ];
        
        // Extrair todas as categorias únicas dos eventos
        let todasCategorias = new Set();
        
        // Processar eventos futuros
        const eventosFuturosProcessados = eventosFuturos.map(evento => {
          const categoria = categoriasDisponiveis[Math.floor(Math.random() * categoriasDisponiveis.length)];
          todasCategorias.add(categoria);
          
          // Gerar URL de imagem padrão caso não exista imagem_url
          const imagemEvento = evento.imagem_url || `https://source.unsplash.com/random/900x600/?${categoria.toLowerCase()}&${evento.id}`;
          
          return {
            ...evento,
            name: evento.nome,
            description: evento.descricao,
            location: evento.local,
            startDate: evento.data_inicio,
            endDate: evento.data_fim,
            category: categoria,
            exhibitors: Math.floor(Math.random() * 300) + 50, // Simulação de número de expositores
            expectedVisitors: Math.floor(Math.random() * 20000) + 5000, // Simulação de visitantes esperados
            image: imagemEvento // Usa a imagem do banco de dados ou uma imagem padrão
          };
        });
        
        // Processar eventos passados
        const eventosPassadosProcessados = eventosAnteriores.map(evento => {
          const categoria = categoriasDisponiveis[Math.floor(Math.random() * categoriasDisponiveis.length)];
          todasCategorias.add(categoria);
          
          // Gerar URL de imagem padrão caso não exista imagem_url
          const imagemEvento = evento.imagem_url || `https://source.unsplash.com/random/900x600/?${categoria.toLowerCase()}&${evento.id}`;
          
          return {
            ...evento,
            name: evento.nome,
            description: evento.descricao,
            location: evento.local,
            startDate: evento.data_inicio,
            endDate: evento.data_fim,
            category: categoria,
            exhibitors: Math.floor(Math.random() * 300) + 50, // Simulação de número de expositores
            visitors: Math.floor(Math.random() * 20000) + 5000, // Simulação de visitantes reais
            image: imagemEvento // Usa a imagem do banco de dados ou uma imagem padrão
          };
        });
        
        setEventos(eventosFuturosProcessados);
        setEventosPassados(eventosPassadosProcessados);
        setCategorias(Array.from(todasCategorias).sort());
        
      } catch (error) {
        console.error('Erro ao buscar eventos:', error);
        setError('Não foi possível carregar os eventos. Por favor, tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    }
    
    fetchEventos();
  }, []);
  
  // Formatação de data
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('pt-BR', options);
  };
  
  // Formatação de números
  const formatNumber = (number) => {
    return new Intl.NumberFormat('pt-BR').format(number);
  };
  
  // Filtrar eventos
  const filteredEvents = eventos.filter(event => {
    // Filtrar por categoria
    if (filter !== 'all' && event.category !== filter) {
      return false;
    }
    
    // Filtrar por busca
    if (searchQuery && !event.name.toLowerCase().includes(searchQuery.toLowerCase()) && 
        !event.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    return true;
  });
  
  // Ir para página de detalhes do evento
  const goToEventDetails = (eventId) => {
    navigate(`/dashboard/events/${eventId}`);
  };
  
  // Ir para página de inscrição no evento
  const registerForEvent = (eventId) => {
    navigate(`/dashboard/events/${eventId}/register`);
  };
  
  // Ir para página do calendário
  const goToCalendar = () => {
    navigate('/dashboard/events/calendar');
  };
  
  // Determinar o status do evento para exibição
  const getEventStatus = (status) => {
    switch (status) {
      case 'ativo':
        return { bg: 'success', text: 'Inscrições Abertas' };
      case 'pendente':
        return { bg: 'warning', text: 'Inscrições em Breve' };
      case 'encerrado':
        return { bg: 'danger', text: 'Inscrições Encerradas' };
      default:
        return { bg: 'info', text: 'Confira' };
    }
  };
  
  // Renderizar componente de imagem (lida com URLs e Base64)
  const renderEventImage = (imageSource, grayscale = false) => {
    // Caso não tenha imagem
    if (!imageSource) {
      return (
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#f8f9fa',
            height: '100%',
            width: '100%'
          }}
        >
          <FaImage size={50} color="#adb5bd" />
        </div>
      );
    }

    // Verifica se é Base64
    const isBase64 = imageSource.startsWith('data:');
    
    return (
      <Card.Img 
        variant="top" 
        src={imageSource} 
        style={{ 
          objectFit: 'cover', 
          height: '100%', 
          width: '100%',
          filter: grayscale ? 'grayscale(40%)' : 'none' 
        }}
        onError={(e) => {
          // Fallback se a imagem falhar ao carregar
          e.target.onerror = null;
          e.target.src = 'https://via.placeholder.com/900x600?text=Evento';
        }}
      />
    );
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mb-0">Eventos</h1>
        <Button 
          variant="outline-primary" 
          onClick={goToCalendar}
          className="d-flex align-items-center"
        >
          <FaCalendarAlt className="me-2" />
          Ver Calendário Completo
        </Button>
      </div>
      
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" variant="primary" className="mb-3" />
          <p>Carregando eventos...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : (
        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4"
        >
          <Tab eventKey="upcoming" title="Eventos Futuros">
            <div className="mb-4">
              <Row>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Filtrar por categoria</Form.Label>
                    <Form.Select 
                      className="w-auto"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                    >
                      <option value="all">Todas as categorias</option>
                      {categorias.map((categoria, index) => (
                        <option key={index} value={categoria}>{categoria}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Buscar eventos</Form.Label>
                    <div className="d-flex">
                      <Form.Control
                        type="text"
                        placeholder="Buscar por nome ou descrição"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      <Button variant="dark" className="ms-2">
                        <FaSearch />
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </div>
            
            {filteredEvents.length === 0 ? (
              <Alert variant="info">
                Nenhum evento encontrado com os filtros selecionados.
              </Alert>
            ) : (
              <Row>
                {filteredEvents.map(event => {
                  const eventStatus = getEventStatus(event.status);
                  return (
                    <Col lg={4} md={6} className="mb-4" key={event.id}>
                      <Card className="h-100 shadow-sm">
                        <div style={{ height: '200px', overflow: 'hidden' }}>
                          {renderEventImage(event.image)}
                        </div>
                        <Card.Body className="d-flex flex-column">
                          <div className="mb-2">
                            <Badge bg="primary">{event.category}</Badge>
                            <Badge bg={eventStatus.bg} text={eventStatus.bg === 'warning' ? 'dark' : 'white'} className="ms-2">
                              {eventStatus.text}
                            </Badge>
                          </div>
                          <Card.Title>{event.name}</Card.Title>
                          <Card.Text className="text-muted">{event.description}</Card.Text>
                          
                          <div className="mt-3">
                            <div className="d-flex align-items-center mb-2">
                              <FaCalendarAlt className="text-primary me-2" />
                              <span>{formatDate(event.startDate)} a {formatDate(event.endDate)}</span>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                              <FaMapMarkerAlt className="text-danger me-2" />
                              <span>{event.location}</span>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                              <FaBuilding className="text-success me-2" />
                              <span>
                                {event.status === 'pendente' ? 'Abertura em breve' : `${formatNumber(event.exhibitors)} expositores confirmados`}
                              </span>
                            </div>
                            <div className="d-flex align-items-center">
                              <FaUsers className="text-info me-2" />
                              <span>Público esperado: {formatNumber(event.expectedVisitors)} visitantes</span>
                            </div>
                          </div>
                          
                          <div className="mt-auto pt-3">
                            <div className="d-flex justify-content-between">
                              <Button 
                                variant="outline-primary" 
                                onClick={() => goToEventDetails(event.id)}
                              >
                                <FaInfoCircle className="me-2" />
                                Detalhes
                              </Button>
                              
                              <Button 
                                variant="success" 
                                onClick={() => registerForEvent(event.id)}
                                disabled={event.status === 'pendente' || event.status === 'encerrado'}
                              >
                                <FaTicketAlt className="me-2" />
                                Participar
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Tab>
          
          <Tab eventKey="past" title="Eventos Passados">
            {eventosPassados.length === 0 ? (
              <Alert variant="info">
                Não há eventos passados para exibir.
              </Alert>
            ) : (
              <Row>
                {eventosPassados.map(event => (
                  <Col lg={4} md={6} className="mb-4" key={event.id}>
                    <Card className="h-100 shadow-sm">
                      <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                        {renderEventImage(event.image, true)}
                        <div 
                          style={{ 
                            position: 'absolute', 
                            top: 0, 
                            left: 0, 
                            width: '100%', 
                            height: '100%', 
                            background: 'rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Badge 
                            bg="secondary" 
                            style={{ 
                              fontSize: '1.2rem', 
                              padding: '0.5rem 1rem' 
                            }}
                          >
                            Concluído
                          </Badge>
                        </div>
                      </div>
                      <Card.Body className="d-flex flex-column">
                        <div className="mb-2">
                          <Badge bg="secondary">{event.category}</Badge>
                        </div>
                        <Card.Title>{event.name}</Card.Title>
                        <Card.Text className="text-muted">{event.description}</Card.Text>
                        
                        <div className="mt-3">
                          <div className="d-flex align-items-center mb-2">
                            <FaCalendarAlt className="text-secondary me-2" />
                            <span>{formatDate(event.startDate)} a {formatDate(event.endDate)}</span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <FaMapMarkerAlt className="text-secondary me-2" />
                            <span>{event.location}</span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <FaBuilding className="text-secondary me-2" />
                            <span>{formatNumber(event.exhibitors)} expositores participantes</span>
                          </div>
                          <div className="d-flex align-items-center">
                            <FaUsers className="text-secondary me-2" />
                            <span>{formatNumber(event.visitors)} visitantes</span>
                          </div>
                        </div>
                        
                        <div className="mt-auto pt-3">
                          <Button 
                            variant="outline-secondary" 
                            onClick={() => goToEventDetails(event.id)}
                            className="w-100"
                          >
                            <FaInfoCircle className="me-2" />
                            Ver relatório do evento
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Tab>
        </Tabs>
      )}
      
      <Alert variant="info" className="mt-4">
        <div className="d-flex">
          <FaInfoCircle size={24} className="me-3 mt-1" />
          <div>
            <h5>Informações sobre participação</h5>
            <p className="mb-0">
              Para participar dos eventos como expositor, é necessário realizar a inscrição com antecedência.
              O prazo para inscrição se encerra geralmente 30 dias antes do início do evento.
              Após a inscrição, você poderá selecionar seu estande e itens adicionais no catálogo de produtos.
            </p>
          </div>
        </div>
      </Alert>
    </Container>
  );
};

export default EventsPage;